import {
	Box,
	Button,
	HStack,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import {
	ACCOUNT_API_COMPANIES,
	ACCOUNT_API_GET_ACCOUNTS_BY_COMPANY,
	BUNDLES_GET_BY_COMPANY,
	SUBSCRIPTIONS_GET_BY_COMPANY,
	SYSTEM_ENTITIES_GET_BYCOMPANY,
} from 'apiRoutes';
import { Company } from 'domain/company/Company';
import { FunctionComponent, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SectionDivider from 'components/common/SectionDivider';
import SubscriptionTable from 'components/subscription/SubscriptionTable';
import BundleTable from 'components/subscription/BundleTable';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { Subscription } from 'domain/subscription/interfaces/Subscription';
import { SubscriptionBundle } from 'domain/subscription/interfaces/SubscriptionBundle';
import BackButton from 'components/common/BackButton/BackButton';
import { MontelAccount } from 'domain/account/interfaces';
import { CompanyDetails, EditableCompanyDetails } from './CompanyDetails';
import { RequestLoader } from 'components/common/Loader';
import { CompanyAuditLog } from './CompanyAuditLog';
import { SystemEntity } from 'domain/systemEntity/interfaces/SystemEntity';
import { subscriptionTableColumns } from 'components/subscription/subscriptionTableColumns';
import { bundleTableColumns } from 'components/subscription/bundleTableColumns';
import { Table } from 'components/tables/Table';
import { systemEntitiesTableColumns } from 'components/systemEntities/SystemEntityTable/systemEntitiesTableColumns';
import { CompanyUserTable } from './CompanyUserTable';
import { RequestStatus } from 'types/RequestStatus';
import { NotFound } from 'components/common/NotFound/NotFound';
import { AdminRole } from 'domain/account/enums/AdminRole';
import useAuthRoles from 'hooks/useAuthRoles';
import { ConditionallyDisabled } from 'components/common/ConditionallyDisabled/ConditionallyDisabled';
import { isPayingCustomer } from 'domain/subscription/isPayingCustomer';

const CompanyProfile: FunctionComponent = () => {
	const { id } = useParams();
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();

	const { data: subscriptions, status: subscriptionsStatus } = useGetOnMount<
		Subscription[]
	>(`${SUBSCRIPTIONS_GET_BY_COMPANY}?companyId=${id}`);

	const { data: bundles, status: bundlesStatus } = useGetOnMount<
		SubscriptionBundle[]
	>(`${BUNDLES_GET_BY_COMPANY}?companyId=${id}`);

	const subscriptionColumns = useMemo(
		() => [
			subscriptionTableColumns.application(),
			subscriptionTableColumns.plan(),
			subscriptionTableColumns.status(),
			subscriptionTableColumns.startDate(),
			subscriptionTableColumns.endDate(),
			subscriptionTableColumns.seats(),
			subscriptionTableColumns.type(),
			subscriptionTableColumns.description(),
			subscriptionTableColumns.expandedIndicator(),
		],
		[subscriptions],
	);

	const bundleColumns = useMemo(
		() => [
			bundleTableColumns.product(),
			bundleTableColumns.type(),
			bundleTableColumns.status(),
			bundleTableColumns.startDate(),
			bundleTableColumns.endDate(),
			bundleTableColumns.seats(),
			bundleTableColumns.description(),
		],
		[bundles],
	);

	const {
		data: company,
		status,
		getData: getCompany,
		error,
	} = useGetOnMount<Company>(
		`${ACCOUNT_API_COMPANIES}/GetCompany?companyId=${id}`,
	);

	const { data: accounts, status: accountsStatus } = useGetOnMount<
		MontelAccount[]
	>(`${ACCOUNT_API_GET_ACCOUNTS_BY_COMPANY}?companyId=${id}`);

	const { data: systemEntities, status: systemEntitiesStatus } =
		useGetOnMount<SystemEntity[]>(
			`${SYSTEM_ENTITIES_GET_BYCOMPANY}?companyId=${id}`,
		);

	const systemEntityColumns = useMemo(
		() => [
			systemEntitiesTableColumns.name(),
			systemEntitiesTableColumns.created(),
		],
		[systemEntities],
	);

	const navigate = useNavigate();

	if (status === RequestStatus.NOT_FOUND && typeof id === 'string') {
		return (
			<NotFound title="Company Not Found" error={error}>
				<CompanyAuditLog companyId={parseInt(id)} />
			</NotFound>
		);
	}

	const companyIsPayingCustomer = isPayingCustomer(subscriptions);

	const isAddSubscriptionDisabled =
		isRestrictedToRole(AdminRole.SALES) && !companyIsPayingCustomer;

	return (
		<Box bg="bgMainContainer">
			<BackButton />
			<RequestLoader h="500px" status={status}>
				{company && (
					<Box bg="bgMainContainer">
						{isEditing ? (
							<EditableCompanyDetails
								company={company}
								setIsEditing={setIsEditing}
								onSuccessfulSubmit={async () => {
									await getCompany();
								}}
							/>
						) : (
							<CompanyDetails
								company={company}
								setIsEditing={setIsEditing}
								hasUsers={!!accounts?.length}
								hasSubscriptions={!!subscriptions?.length}
							/>
						)}
						<SectionDivider />
						<BundleTable
							columns={bundleColumns}
							requestStatus={bundlesStatus}
							bundles={bundles}
							onRowClicked={(bundle) =>
								navigate(`/subscription-bundles/${bundle.id}`)
							}
						/>
						<HStack fullWidth justify="flex-start">
							<ConditionallyDisabled
								disableIf={isRestrictedToRole(AdminRole.SALES)}
								tooltip="You do not have permission to create subscription bundles."
							>
								<Button
									text="Add bundle"
									onClick={() =>
										navigate(
											`/subscription-bundles/create?companyId=${company.id}`,
										)
									}
									isDisabled={isRestrictedToRole(
										AdminRole.SALES,
									)}
								/>
							</ConditionallyDisabled>
						</HStack>
						<SectionDivider />
						<VStack>
							<SubscriptionTable
								columns={subscriptionColumns}
								heading="Subscriptions"
								requestStatus={subscriptionsStatus}
								subscriptions={subscriptions}
								excludeBundledSubscriptions
							/>
							<ConditionallyDisabled
								disableIf={isAddSubscriptionDisabled}
								tooltip={
									'Sales role can only create a subscription for companies that are paying customers'
								}
							>
								<Button
									text="Add subscription"
									onClick={() =>
										navigate(
											`/subscriptions/create?companyId=${company.id}`,
										)
									}
								/>
							</ConditionallyDisabled>
						</VStack>
						<SectionDivider />
						<CompanyUserTable
							requestStatus={accountsStatus}
							accounts={accounts}
							header={'Accounts'}
							withPagination={accounts?.length > 10}
							withSearch={accounts?.length > 10}
							companyId={company.id}
						/>
						<SectionDivider />
						<Table<SystemEntity>
							header={'System Entities'}
							columns={systemEntityColumns}
							containerGap="xs"
							requestStatus={systemEntitiesStatus}
							data={systemEntities}
							onRowClicked={(row: SystemEntity) => {
								navigate(`/systementities/${row.id}`);
							}}
							noDataText="No system entities connected to this company."
						/>
						<SectionDivider />
						<CompanyAuditLog companyId={company.id} />
					</Box>
				)}
			</RequestLoader>
		</Box>
	);
};

export default CompanyProfile;
